import { useEffect, useRef, useCallback } from 'react';

interface UseAutoLogoutProps {
  isAuthenticated: boolean;
  onLogout: () => void;
  timeoutMinutes?: number;
  warningMinutes?: number;
  onWarning?: () => void;
}

export const useAutoLogout = ({
  isAuthenticated,
  onLogout,
  timeoutMinutes = 15,
  warningMinutes = 2,
  onWarning
}: UseAutoLogoutProps) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Convert minutes to milliseconds
  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningTimeoutMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    if (!isAuthenticated) {
      return;
    }

    // Set warning timer (fires 2 minutes before logout)
    if (onWarning && warningMinutes > 0) {
      warningTimeoutRef.current = setTimeout(() => {
        onWarning();
      }, warningTimeoutMs);
    }

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      onLogout();
    }, timeoutMs);
  }, [isAuthenticated, onLogout, onWarning, timeoutMs, warningTimeoutMs, warningMinutes]);

  // Activity event handler
  const handleActivity = useCallback(() => {
    if (isAuthenticated) {
      resetTimer();
    }
  }, [isAuthenticated, resetTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timers when not authenticated
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      return;
    }

    // Start the timer when authenticated
    resetTimer();

    // Activity events to listen for
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      // Clear timers
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [isAuthenticated, handleActivity, resetTimer]);

  // Return function to manually extend session
  const extendSession = useCallback(() => {
    if (isAuthenticated) {
      resetTimer();
    }
  }, [isAuthenticated, resetTimer]);

  return { extendSession };
};